import api from './client';

export interface HealthReportParams {
  reportType: 'bird' | 'flock' | 'analytics' | 'vaccination' | 'treatment';
  format: 'pdf' | 'excel' | 'csv';
  birdId?: string;
  startDate?: string;
  endDate?: string;
  filters?: Record<string, any>;
}

export interface BirdHealthReport {
  bird: {
    id: string;
    bandId: string;
    healthStatus: string;
  };
  healthScore: number;
  recommendations: string[];
  testHistory: any[];
  vaccinationHistory: any[];
  treatmentHistory: any[];
}

export interface FlockHealthReport {
  reportDate: string;
  totalBirds: number;
  healthStatusDistribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  recommendations: string[];
  alerts: any[];
  mortalityRate: number;
  morbidityRate: number;
}

export interface HealthAnalyticsReport {
  mortalityRate: number;
  morbidityRate: number;
  averageHealthScore: number;
  testingFrequency: number;
  vaccinationCompliance: number;
  treatmentSuccessRate: number;
  costAnalysis: {
    totalHealthCosts: number;
    averageCostPerBird: number;
    costByCategory: Record<string, number>;
  };
  seasonalTrends: any[];
}

export const healthReportsApi = {
  // Generate bird health report
  async generateBirdHealthReport(birdId: string): Promise<BirdHealthReport> {
    const response = await api.get(`reports/health/bird/${birdId}`);
    return response.data as BirdHealthReport;
  },

  // Generate flock health report
  async generateFlockHealthReport(): Promise<FlockHealthReport> {
    const response = await api.get('reports/health/flock');
    return response.data as FlockHealthReport;
  },

  // Generate health analytics report
  async generateHealthAnalytics(): Promise<HealthAnalyticsReport> {
    const response = await api.get('reports/health/analytics');
    return response.data as HealthAnalyticsReport;
  },

  // Generate vaccination compliance report
  async generateVaccinationReport(): Promise<any> {
    const response = await api.get('reports/health/vaccinations/compliance');
    return response.data as any;
  },

  // Generate treatment effectiveness report
  async generateTreatmentReport(): Promise<any> {
    const response = await api.get('reports/health/treatments/effectiveness');
    return response.data as any;
  },

  // Generate custom report with parameters
  async generateCustomReport(params: HealthReportParams): Promise<any> {
    const response = await api.post('reports/health/generate', params);
    return response.data as any;
  },

  // Download report file (sync — may timeout on large reports)
  async downloadReport(reportType: string, format: string, params?: any): Promise<Blob> {
    const baseUrl = window.location.origin.includes('localhost:3005') 
      ? 'http://localhost:3001' 
      : window.location.origin;
    const fullUrl = `${baseUrl}/reports/health/download`;
    
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        reportType,
        format,
        ...params
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download report: ${response.statusText}`);
    }
    
    return response.blob();
  },

  // Get health statistics for reports
  async getHealthStats(startDate?: string, endDate?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await api.get('reports/health', params);
    return response.data as any;
  },

  async enqueueReportJob(params: HealthReportParams & { birdId?: string }) {
    const response = await api.post('reports/health/jobs', params);
    return response.data as { id: string; status: string; progress: number };
  },

  async getReportJob(jobId: string) {
    const response = await api.get(`reports/health/jobs/${jobId}`);
    return response.data as { id: string; status: string; progress: number; error?: string };
  },

  async downloadReportJob(jobId: string): Promise<Blob> {
    const baseUrl = window.location.origin.includes('localhost:3005')
      ? 'http://localhost:3001'
      : window.location.origin;
    const token = localStorage.getItem('token');
    const response = await fetch(`${baseUrl}/api/reports/health/jobs/${jobId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Failed to download report job');
    return response.blob();
  },

  async downloadReportAsync(
    reportType: string,
    format: string,
    params?: { birdId?: string },
    onProgress?: (progress: number, status: string) => void,
  ): Promise<Blob> {
    const job = await this.enqueueReportJob({ reportType: reportType as HealthReportParams['reportType'], format: format as HealthReportParams['format'], ...params });
    for (let i = 0; i < 60; i++) {
      const status = await this.getReportJob(job.id);
      onProgress?.(status.progress, status.status);
      if (status.status === 'COMPLETED') return this.downloadReportJob(job.id);
      if (status.status === 'FAILED') throw new Error(status.error ?? 'Report failed');
      await new Promise((r) => setTimeout(r, 500));
    }
    throw new Error('Report timed out');
  },
};